'use client';
import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { parseImagePosition } from '@/utils/parseImagePosition';
import {
    getReviewSourceAlt,
    normalizeReviewSourceIcon,
} from '@/utils/reviewSourceAlt';
import Carousel from 'nuka-carousel';
import { useEffect, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

const PACK_TOKENS = {
    classic: [],
    duotone: ['fa-duotone'],
    sharp: ['fa-sharp'],
    'sharp-duotone': ['fa-sharp-duotone', 'fa-sharp', 'fa-duotone'],
};

const normalizeStyleForPack = (style, pack) => {
    if (pack === 'brands') return 'brands';
    const allowed = ['solid', 'regular', 'light', 'thin'];
    return allowed.includes(style) ? style : 'solid';
};

const buildIconClass = (name, pack, style) => {
    const tokens = [];
    const normalizedPack = pack || 'classic';
    const normalizedStyle = normalizeStyleForPack(style, normalizedPack);
    (PACK_TOKENS[normalizedPack] || []).forEach((t) => tokens.push(t));
    if (normalizedPack !== 'brands') tokens.push(`fa-${normalizedStyle}`);
    tokens.push(`fa-${name}`);
    const seen = new Set();
    return tokens
        .filter((t) => {
            if (!t || seen.has(t)) return false;
            seen.add(t);
            return true;
        })
        .join(' ');
};

const FEATURE_ICON_SIZE_CLASSES = {
    default: '',
    'x-small': 'text-lg sm:text-lg lg:text-2xl',
    small: 'text-xl sm:text-2xl lg:text-3xl',
    medium: 'text-2xl sm:text-3xl lg:text-4xl',
    large: 'text-3xl sm:text-4xl lg:text-5xl',
    'x-large': 'text-4xl sm:text-5xl lg:text-6xl',
};

const getFeatureIconSizeClass = (value) =>
    FEATURE_ICON_SIZE_CLASSES[
        String(value || 'default')
            .toLowerCase()
            .trim()
    ] || '';

const useSiteIconOverride = () => {
    const [pack, setPack] = useState('classic');
    const [style, setStyle] = useState('solid');

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const root = document.documentElement;
        const read = () => {
            const nextPack = root.dataset.faPackOverride || 'classic';
            const nextStyle =
                root.dataset.faStyleOverride &&
                ['solid', 'regular', 'light', 'thin'].includes(
                    root.dataset.faStyleOverride
                )
                    ? root.dataset.faStyleOverride
                    : 'solid';
            setPack(nextPack);
            setStyle(nextStyle);
        };
        read();
        const observer = new MutationObserver(() => read());
        observer.observe(root, {
            attributes: true,
            attributeFilter: [
                'data-fa-pack-override',
                'data-fa-style-override',
            ],
        });
        return () => observer.disconnect();
    }, []);

    return { pack, style };
};

export default function Reviews(props) {
    const {
        Tagline,
        Header,
        Reviews,
        FeatureIcon = 'stars',
        FeatureIconPack = '',
        FeatureIconStyle = '',
        FeatureIconSize = 'default',
        ButtonLink,
        ButtonText,
        ButtonOpenInNewTab,
        Showcase,
        ColorScheme = 'light',
        BackgroundImageUrl,
        BackgroundImagePosition = '50',
        BackgroundBlend = false,
        VerticalSpacing = 'medium',
    } = props;

    let backgroundClass,
        reviewtextColor,
        reviewName,
        headingColor,
        arrowColor,
        overlayClass,
        starAlign,
        buttonColor;
    const vSpace = getVerticalSpacing(VerticalSpacing);
    const { pack: sitePack, style: siteStyle } = useSiteIconOverride();
    const effectiveIconPack =
        FeatureIconPack && FeatureIconPack.trim().length
            ? FeatureIconPack.trim()
            : sitePack;
    const effectiveIconStyle =
        FeatureIconStyle && FeatureIconStyle.trim().length
            ? FeatureIconStyle.trim()
            : siteStyle;
    const featureIconSizeClass = getFeatureIconSizeClass(FeatureIconSize);
    const featureIcon =
        String(FeatureIcon || 'stars').toLowerCase() === 'quote'
            ? 'quote'
            : 'stars';

    const scheme = getColorScheme(ColorScheme.trim(), props.GradientDirection);
    backgroundClass = scheme.backgroundColor || 'bg-white';
    headingColor = scheme.headerColor || 'text-primary-900';
    reviewtextColor = scheme.textColor || 'text-base text-neutral-800';
    const quoteIconColor = scheme.iconColor || 'text-primary-700';
    const starIconColor = scheme.iconColor || 'text-primary-700';

    overlayClass = scheme.overlayColor || '';
    buttonColor = scheme.thirdButton || 'outline';
    arrowColor = headingColor?.includes('white')
        ? 'text-white'
        : 'text-neutral-800';
    starAlign = 'items-left';

    switch (ColorScheme.trim()) {
        case 'background-light':
            headingColor = 'text-neutral-800';
            backgroundClass = 'bg-center bg-cover bg-blend-overlay';
            reviewtextColor = 'text-base text-neutral-800';
            backgroundClass = 'bg-center bg-cover bg-blend-overlay';
            overlayClass = 'bg-white bg-opacity-70';

            arrowColor = 'text-neutral-800';
            buttonColor = 'outline';
            starAlign = 'items-left';
            break;
        case 'background-dark':
            headingColor = 'text-white';
            backgroundClass = 'bg-center bg-cover bg-blend-overlay';
            overlayClass = 'bg-neutral-800 bg-opacity-70';
            reviewtextColor = 'text-base text-white';

            buttonColor = 'outlineDark';
            arrowColor = 'text-white';
            starAlign = 'items-left';
            break;
        case 'background-dark-no-filter':
            headingColor = 'text-neutral-800';
            backgroundClass = 'bg-center bg-cover bg-blend-overlay';
            overlayClass = '';
            reviewtextColor = 'text-base text-white';

            buttonColor = 'outlineDark';
            arrowColor = 'text-white';
            starAlign = 'items-left';
            break;
        case 'background-light-no-filter':
            headingColor = 'text-white';
            backgroundClass = 'bg-center bg-cover bg-blend-overlay';
            overlayClass = '';
            reviewtextColor = 'text-base text-white';

            arrowColor = 'text-white';
            buttonColor = 'outlineDark';
            starAlign = 'items-left';
            break;
        default:
            // use centralized getColorScheme defaults set above
            break;
    }

    let number = '2';

    if (Showcase) {
        switch (`${Showcase}`.trim()) {
            case '3':
                number = 3;
                break;
            case '1':
                number = 1;
                break;
            default:
                '2';
                number = 2;
                break;
        }
    }

    if (
        ColorScheme.trim() === 'primary-no-box' ||
        ColorScheme.trim() === 'secondary-no-box'
    ) {
        number = 1;
    }

    const hasBackgroundImage = Boolean(BackgroundImageUrl);
    const { x: bgPosX, y: bgPosY } = parseImagePosition(
        BackgroundImagePosition
    );

    const containerStyle = {
        backgroundImage: hasBackgroundImage ? `url(${BackgroundImageUrl})` : '',
        backgroundPosition: hasBackgroundImage
            ? `${bgPosX}% ${bgPosY}%`
            : undefined,
    };

    const [slidesToShow, setSlidesToShow] = useState(number);

    const updateSlidesToShow = () => {
        if (window.innerWidth < 1024) {
            setSlidesToShow(1);
        } else {
            setSlidesToShow(number);
        }
    };

    useEffect(() => {
        updateSlidesToShow();
        window.addEventListener('resize', updateSlidesToShow);
        return () => {
            window.removeEventListener('resize', updateSlidesToShow);
        };
    }, [number]);

    const { hasAny: hasCTA, element: CTAButtons } = getCTA(
        {
            CTA: ButtonText,
            CustomCTALink: ButtonLink,
            CTAOpenInNewTab: ButtonOpenInNewTab,
        },
        {
            primaryVariant: buttonColor,
            buttonAlign: 'justify-center',
            wrapSpacing: 'mt-10 lg:mt-16',
            buttonsClassName: 'flex items-center',
            size: 'md',
        }
    );

    const taglineSpacing = Header ? '' : 'mb-12 lg:mb-16';

    const blendEnabled =
        typeof BackgroundBlend === 'string'
            ? BackgroundBlend.trim().toLowerCase() === 'true'
            : Boolean(BackgroundBlend);
    const shouldBlend = hasBackgroundImage && blendEnabled;

    // Overlay only when not using blend; blend relies on background color + image
    const overlayClassName =
        hasBackgroundImage && !shouldBlend && overlayClass ? overlayClass : '';
    const backgroundBlendClass = hasBackgroundImage
        ? shouldBlend
            ? 'bg-center bg-cover bg-blend-overlay'
            : 'bg-center bg-cover'
        : '';

    return (
        <div
            className={`editable-component relative ${vSpace} ${backgroundClass} ${backgroundBlendClass}`}
            data-json="testimonials"
            style={{ ...scheme.backgroundStyle, ...containerStyle }}
        >
            {overlayClassName && (
                <div
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-0 ${overlayClassName}`}
                    style={scheme.overlayStyle}
                />
            )}

            <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 xl:max-w-none">
                <div className="mx-auto text-center">
                    {Tagline && (
                        <MarkDown
                            components={{
                                p: ({ children, className, ...rest }) => (
                                    <h2
                                        {...rest}
                                        className={[
                                            'mx-auto max-w-5xl font-display text-base uppercase leading-7 tracking-wide',
                                            taglineSpacing,
                                            headingColor,
                                            className,
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                    >
                                        {children}
                                    </h2>
                                ),
                            }}
                        >
                            {Tagline}
                        </MarkDown>
                    )}
                    {Header && (
                        <MarkDown
                            variant="h2"
                            className={`mx-auto mb-12 mt-2 max-w-5xl font-display text-3xl tracking-tight lg:mb-16 ${headingColor} sm:text-4xl sm:leading-tight`}
                        >
                            {Header}
                        </MarkDown>
                    )}
                </div>

                <div className="mx-auto flow-root w-full max-w-7xl">
                    <Carousel
                        renderCenterLeftControls={({
                            previousSlide,
                            previousDisabled,
                        }) => (
                            <button
                                className={`fa-solid fa-angle-left -translate-x-5 text-3xl ${arrowColor} opacity-0 transition-opacity duration-500 ease-in-out hover:opacity-100 md:opacity-20`}
                                onClick={previousSlide}
                                disabled={previousDisabled}
                                aria-label="Previous Slide"
                            ></button>
                        )}
                        renderCenterRightControls={({
                            nextSlide,
                            nextDisabled,
                        }) => (
                            <button
                                className={`fa-solid fa-angle-right translate-x-5 text-3xl ${arrowColor} opacity-0 transition-opacity duration-500 ease-in-out hover:opacity-100 md:opacity-20`}
                                onClick={nextSlide}
                                disabled={nextDisabled}
                                aria-label="Next Slide"
                            ></button>
                        )}
                        renderBottomCenterControls={() => <div></div>}
                        cellAlign="center"
                        cellSpacing={48}
                        className="mx-auto max-w-6xl"
                        disableEdgeSwiping={true}
                        autoplay={true}
                        autoplayInterval={5000}
                        wrapAround={true}
                        slidesToShow={slidesToShow}
                    >
                        {Reviews.map((Review) => (
                            <div
                                key={Review.Author.Name}
                                className="mx-auto flex h-full w-full max-w-3xl flex-col items-center justify-center" // Center horizontally
                                style={{ minHeight: '100%' }} // Vertically center by setting a minimum height
                            >
                                <figure
                                    className={`bg-opacity/20 relative flex h-full w-full flex-col rounded-[min(1.5rem,var(--shape-radius))] p-8 text-sm leading-6 backdrop-blur-xl lg:p-12`}
                                >
                                    <div className="grow">
                                        <div className="transition-all duration-300 ease-in-out">
                                            <blockquote className="text-neutral-800">
                                                <span className={``}>
                                                    {/* Feature icon */}
                                                    <div
                                                        className={`flex ${starAlign}`}
                                                    >
                                                        {featureIcon ===
                                                        'quote' ? (
                                                            <i
                                                                className={`${buildIconClass(
                                                                    'quote-left',
                                                                    effectiveIconPack,
                                                                    effectiveIconStyle
                                                                )} ${quoteIconColor} scale-110 leading-none ${
                                                                    featureIconSizeClass ||
                                                                    'text-lg sm:text-lg lg:text-2xl'
                                                                }`}
                                                                aria-hidden="true"
                                                            />
                                                        ) : (
                                                            [...Array(5)].map(
                                                                (_, i) => (
                                                                    <i
                                                                        key={i}
                                                                        className={`${buildIconClass(
                                                                            'star',
                                                                            effectiveIconPack,
                                                                            effectiveIconStyle
                                                                        )} mx-1 ${starIconColor} ${
                                                                            featureIconSizeClass ||
                                                                            'sm:text-lg lg:text-xl'
                                                                        }`}
                                                                        aria-hidden="true"
                                                                    />
                                                                )
                                                            )
                                                        )}
                                                    </div>
                                                </span>
                                                <MarkDown
                                                    components={{
                                                        p: ({
                                                            children,
                                                            className,
                                                            ...rest
                                                        }) => (
                                                            <p
                                                                {...rest}
                                                                className={[
                                                                    'mt-7 line-clamp-5 whitespace-pre-wrap',
                                                                    reviewtextColor,
                                                                    className,
                                                                ]
                                                                    .filter(
                                                                        Boolean
                                                                    )
                                                                    .join(' ')}
                                                            >
                                                                {children}
                                                            </p>
                                                        ),
                                                    }}
                                                >
                                                    {'"' +
                                                        (Review?.Body ?? '') +
                                                        '"'}
                                                </MarkDown>
                                            </blockquote>
                                        </div>
                                    </div>
                                    <figcaption
                                        className={`${reviewName} ${reviewtextColor} mt-8 flex items-end`}
                                    >
                                        <img
                                            className="h-9 w-9 rounded-full bg-white p-1 ring-2 ring-white"
                                            src={normalizeReviewSourceIcon(
                                                Review.Author.Icon
                                            )}
                                            alt={getReviewSourceAlt(
                                                Review.Author.Icon
                                            )}
                                        />
                                        <MarkDown
                                            wrapperClassName="ml-4 text-base font-semibold"
                                            className="inline"
                                            components={{
                                                p: ({
                                                    children,
                                                    className,
                                                    ...rest
                                                }) => (
                                                    <span
                                                        {...rest}
                                                        className={[
                                                            'inline whitespace-pre-wrap',
                                                            className,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' ')}
                                                    >
                                                        {children}
                                                    </span>
                                                ),
                                            }}
                                        >
                                            {Review.Author.Name}
                                        </MarkDown>
                                    </figcaption>
                                </figure>
                            </div>
                        ))}
                    </Carousel>

                    {hasCTA && CTAButtons}
                </div>
            </div>
        </div>
    );
}
